import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../api";

export type PhpHandlerRow = {
  id: string;
  extension: string;
  handlerType: "CGI" | "PHP-FPM" | "DSO" | "suPHP" | "LSAPI";
  handler: string;
  isDefault?: boolean;
};

const HANDLER_TYPES: PhpHandlerRow["handlerType"][] = [
  "CGI",
  "PHP-FPM",
  "DSO",
  "suPHP",
  "LSAPI"
];

const HANDLER_OPTIONS = [
  "php74-cgi",
  "php80-cgi",
  "php81-cgi",
  "php82-cgi",
  "php83-cgi",
  "php84-cgi",
  "php74-fpm",
  "php80-fpm",
  "php81-fpm",
  "php82-fpm",
  "php83-fpm",
  "php84-fpm",
  "libphp",
  "suphp"
];

/**
 * Softaculous/Webuzo-style Apache PHP Handlers table.
 */
export function ApachePhpHandlersPage() {
  const [handlers, setHandlers] = useState<PhpHandlerRow[]>([]);
  const [defaultExtension, setDefaultExtension] = useState("php");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<PhpHandlerRow | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api<{
        handlers: PhpHandlerRow[];
        defaultExtension: string;
      }>("/api/v1/server-config/php-handlers");
      setHandlers(res.handlers || []);
      setDefaultExtension(res.defaultExtension || "php");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saveAll = async (next: PhpHandlerRow[], defExt: string) => {
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api<{
        handlers: PhpHandlerRow[];
        defaultExtension: string;
      }>("/api/v1/server-config/php-handlers", {
        method: "PUT",
        body: JSON.stringify({
          handlers: next,
          defaultExtension: defExt
        })
      });
      setHandlers(res.handlers || next);
      setDefaultExtension(res.defaultExtension || defExt);
      // Apply to agent
      await api("/api/v1/server-config/web-apply", {
        method: "POST",
        body: JSON.stringify({})
      }).catch(() => null);
      setMsg("PHP handlers saved");
      setEditing(null);
      setAdding(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const setAsDefault = async (ext: string) => {
    const next = handlers.map((h) => ({
      ...h,
      isDefault: h.extension === ext
    }));
    await saveAll(next, ext);
  };

  const removeRow = async (id: string) => {
    if (!confirm("Remove this handler?")) return;
    const next = handlers.filter((h) => h.id !== id);
    const def =
      defaultExtension && next.some((h) => h.extension === defaultExtension)
        ? defaultExtension
        : next[0]?.extension || "php";
    await saveAll(next, def);
  };

  const submitEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const ext = editing.extension.replace(/^\./, "").trim();
    if (!ext) {
      setError("Extension is required");
      return;
    }
    const row: PhpHandlerRow = {
      ...editing,
      id: editing.id || `h-${ext}-${Date.now()}`,
      extension: ext,
      handler: editing.handler.trim(),
      handlerType: editing.handlerType
    };
    let next: PhpHandlerRow[];
    if (handlers.some((h) => h.id === row.id)) {
      next = handlers.map((h) => (h.id === row.id ? row : h));
    } else if (handlers.some((h) => h.extension === row.extension)) {
      next = handlers.map((h) =>
        h.extension === row.extension ? { ...row, id: h.id } : h
      );
    } else {
      next = [...handlers, row];
    }
    await saveAll(next, defaultExtension);
  };

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Apache PHP Handlers</h1>
          <p>
            Map file extensions to CGI / PHP-FPM handlers (Softaculous-style)
          </p>
        </div>
        <div className="topbar-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setAdding(true);
              setEditing({
                id: "",
                extension: "",
                handlerType: "PHP-FPM",
                handler: "php83-fpm"
              });
            }}
          >
            + Add Handler
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="card full" style={{ marginBottom: 16 }}>
        <p className="card-meta">
          Notice: To use the <strong>DSO</strong> PHP handler, FastCGI should be
          disabled and MPM Prefork used. RealPanel defaults to{" "}
          <strong>PHP-FPM</strong> per account (recommended).
        </p>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Extension</th>
              <th>Handler type</th>
              <th>Handler</th>
              <th style={{ width: 220 }} />
            </tr>
          </thead>
          <tbody>
            {handlers.length === 0 ? (
              <tr>
                <td colSpan={4} className="faint">
                  No handlers configured
                </td>
              </tr>
            ) : (
              handlers.map((h) => (
                <tr key={h.id}>
                  <td>
                    <span className="mono">
                      .{h.extension}
                      {(h.isDefault || h.extension === defaultExtension) && (
                        <span
                          className="badge status-active"
                          style={{ marginLeft: 8 }}
                        >
                          default
                        </span>
                      )}
                    </span>
                  </td>
                  <td>
                    <span className="badge role-admin">{h.handlerType}</span>
                  </td>
                  <td className="mono faint">{h.handler}</td>
                  <td>
                    <div className="topbar-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          setAdding(false);
                          setEditing({ ...h });
                        }}
                      >
                        Edit
                      </button>
                      {h.extension !== defaultExtension ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() => void setAsDefault(h.extension)}
                        >
                          Set as Default
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => void removeRow(h.id)}
                      >
                        Del
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing ? (
        <form className="panel-form" style={{ marginTop: 16 }} onSubmit={submitEdit}>
          <div className="panel-form-title">
            {adding ? "Add Handler" : `Edit .${editing.extension}`}
          </div>
          <div className="form-grid">
            <div className="field">
              <label>Extension (without dot)</label>
              <input
                className="mono"
                value={editing.extension}
                onChange={(e) =>
                  setEditing({ ...editing, extension: e.target.value })
                }
                placeholder="php8.3"
                required
              />
            </div>
            <div className="field">
              <label>Handler type</label>
              <select
                value={editing.handlerType}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    handlerType: e.target.value as PhpHandlerRow["handlerType"]
                  })
                }
              >
                {HANDLER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Handler</label>
              <select
                value={
                  HANDLER_OPTIONS.includes(editing.handler)
                    ? editing.handler
                    : "__custom__"
                }
                onChange={(e) => {
                  if (e.target.value === "__custom__") return;
                  setEditing({ ...editing, handler: e.target.value });
                }}
              >
                {HANDLER_OPTIONS.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
                <option value="__custom__">Custom…</option>
              </select>
              {!HANDLER_OPTIONS.includes(editing.handler) ? (
                <input
                  className="mono"
                  style={{ marginTop: 8 }}
                  value={editing.handler}
                  onChange={(e) =>
                    setEditing({ ...editing, handler: e.target.value })
                  }
                  placeholder="php83-fpm"
                />
              ) : null}
            </div>
          </div>
          <div className="topbar-actions" style={{ marginTop: 12 }}>
            <button
              type="submit"
              className="btn btn-primary btn-inline"
              disabled={busy}
            >
              {busy ? "Saving…" : "Save Settings"}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setEditing(null);
                setAdding(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </>
  );
}
